import { createDefaultSaveMigrationRegistry } from './SaveMigrationRegistry.js';

export const SAVE_ENVELOPE_VERSION = 1;
export const SAVE_FORMAT = 'serpent-born-wild-save';

const REQUIRED_SESSION_KEYS = Object.freeze([
  'environment',
  'clock',
  'random',
  'simulation',
  'entityWorld',
  'traversalWorld',
  'snakeState',
  'wildlifeSystem',
  'huntingSystem',
  'storySystem',
]);

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertJsonValue(value, name) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    throw new TypeError(`${name} must be JSON serializable`);
  }
}

function assertTimestamp(value, name) {
  if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
    throw new TypeError(`${name} must be a valid timestamp`);
  }
  const normalized = new Date(value).toISOString();
  if (normalized !== value) throw new RangeError(`${name} must use canonical ISO format`);
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

export function computeSaveEnvelopeChecksum(value) {
  const payload = clone(assertPlainObject(value, 'save'));
  delete payload.checksum;
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function normalizeSession(value) {
  const session = clone(assertPlainObject(value, 'save.session'));
  for (const key of REQUIRED_SESSION_KEYS) {
    if (!Object.hasOwn(session, key)) throw new RangeError(`save.session is missing ${key}`);
  }
  assertPlainObject(session.environment, 'save.session.environment');
  assertPlainObject(session.clock, 'save.session.clock');
  assertPlainObject(session.random, 'save.session.random');
  assertPlainObject(session.simulation, 'save.session.simulation');
  assertNonNegativeInteger(session.simulation.nextTick, 'save.session.simulation.nextTick');
  assertPlainObject(session.entityWorld, 'save.session.entityWorld');
  assertPlainObject(session.traversalWorld, 'save.session.traversalWorld');
  if (session.snakeState !== null) assertPlainObject(session.snakeState, 'save.session.snakeState');
  assertPlainObject(session.wildlifeSystem, 'save.session.wildlifeSystem');
  assertPlainObject(session.huntingSystem, 'save.session.huntingSystem');
  assertPlainObject(session.storySystem, 'save.session.storySystem');
  return assertJsonValue(session, 'save.session');
}

export function createSaveEnvelope({
  session,
  metadata = {},
  createdAt = new Date().toISOString(),
  updatedAt = createdAt,
}) {
  const payload = {
    format: SAVE_FORMAT,
    version: SAVE_ENVELOPE_VERSION,
    createdAt: assertTimestamp(createdAt, 'createdAt'),
    updatedAt: assertTimestamp(updatedAt, 'updatedAt'),
    metadata: assertJsonValue(assertPlainObject(metadata, 'metadata'), 'metadata'),
    session: normalizeSession(session),
  };
  return deepFreeze({
    ...payload,
    checksum: computeSaveEnvelopeChecksum(payload),
  });
}

export function normalizeSaveEnvelope(value, {
  migrationRegistry = createDefaultSaveMigrationRegistry(),
} = {}) {
  const parsed = typeof value === 'string' ? JSON.parse(value) : clone(value);
  assertPlainObject(parsed, 'save');
  assertNonNegativeInteger(parsed.version, 'save.version');
  if (parsed.version > SAVE_ENVELOPE_VERSION) {
    throw new RangeError(
      `Save version ${parsed.version} is newer than supported version ${SAVE_ENVELOPE_VERSION}`,
    );
  }

  if (parsed.version === SAVE_ENVELOPE_VERSION) {
    if (typeof parsed.checksum !== 'string'
      || parsed.checksum !== computeSaveEnvelopeChecksum(parsed)) {
      throw new RangeError('Save checksum is invalid');
    }
  } else if (parsed.checksum !== undefined
    && parsed.checksum !== computeSaveEnvelopeChecksum(parsed)) {
    throw new RangeError('Legacy save checksum is invalid');
  }

  const migration = migrationRegistry.migrate(parsed);
  const migrated = clone(migration.value);
  if (migrated.version !== SAVE_ENVELOPE_VERSION) {
    throw new RangeError(`Unsupported save version: ${migrated.version}`);
  }
  if (migrated.format !== SAVE_FORMAT) throw new RangeError('Save format is invalid');

  const payload = {
    format: migrated.format,
    version: migrated.version,
    createdAt: assertTimestamp(migrated.createdAt, 'save.createdAt'),
    updatedAt: assertTimestamp(migrated.updatedAt, 'save.updatedAt'),
    metadata: assertJsonValue(
      assertPlainObject(migrated.metadata, 'save.metadata'),
      'save.metadata',
    ),
    session: normalizeSession(migrated.session),
  };
  const expectedChecksum = computeSaveEnvelopeChecksum(payload);
  if (migration.applied.length === 0) {
    if (migrated.checksum !== expectedChecksum) throw new RangeError('Save checksum is invalid');
  }
  const envelope = deepFreeze({ ...payload, checksum: expectedChecksum });
  return deepFreeze({
    envelope,
    migratedFromVersion: migration.fromVersion === migration.toVersion
      ? null
      : migration.fromVersion,
    appliedMigrations: migration.applied,
  });
}

export function verifySaveEnvelope(value, options = {}) {
  try {
    const normalized = normalizeSaveEnvelope(value, options);
    return deepFreeze({
      ok: true,
      checksum: normalized.envelope.checksum,
      version: normalized.envelope.version,
      migratedFromVersion: normalized.migratedFromVersion,
      appliedMigrations: normalized.appliedMigrations,
      error: null,
    });
  } catch (error) {
    return deepFreeze({
      ok: false,
      checksum: null,
      version: null,
      migratedFromVersion: null,
      appliedMigrations: [],
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
